import { Dispatch } from "redux";
import axios from "axios";
import { QR_X_TYPE, RESEND_NOTIFY_X_TYPE } from "@delorean/_shared/constants";
import { PasswordResetScreen } from "@delorean/forgetPassword/types";
import { LOGIN_RESET_TEMP_PSW_SUCCESS } from "./resetPasswordActions";
import { encryptMoreTxi } from "@delorean/utils/encryptHash";
import { getApiEndpoints, getEnvVar } from "@delorean/utils/env-utils";
import { ErrorType } from "@delorean/_shared/domain/errorType";

export const LOGIN_SUCCESS = "LOGIN_SUCCESS";
export const LOGIN_FAILURE = "LOGIN_FAILURE";
export const LOGIN_REQUEST = "LOGIN_REQUEST";
export const LOGIN_SUCCESS_QR_CODE = "LOGIN_SUCCESS_QR_CODE";
export const SET_SCREEN = "SET_SCREEN";
export const SET_ERROR = "SET_ERROR";
export const LOGIN_CANCEL_PROCEDURE = "LOGIN_CANCEL_PROCEDURE";
export const LOGIN_SESSION_EXPIRED = "LOGIN_SESSION_EXPIRED";
export const RESEND_NOTIFY = "RESEND_NOTIFY";

/*
 * 409 - is always a success response for login or reset psw
 */
export const performLogin = (
  contractCode: string,
  userCode: string,
  password: string,
  moreTxi: string,
  captchaToken: string | undefined,
) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: LOGIN_REQUEST });
    const params = new URLSearchParams();
    const data: { [key: string]: any } = {
      username: `${contractCode}_${userCode}`,
      password,
      grant_type: "PASSWORD",
    };
    Object.keys(data).forEach((key) => {
      const value =
        typeof data[key] === "object" ? JSON.stringify(data[key]) : data[key];
      params.append(key, value);
    });

    const moreTxiBase64Encoded = await encryptMoreTxi(moreTxi);

    await axios
      .post(getApiEndpoints().LOGIN, params, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "x-client-source": "WEB",
          "x-client-risk": moreTxiBase64Encoded,
          ...(captchaToken
            ? {
                "x-captcha-token": captchaToken,
                "x-captcha-kid": `${getEnvVar("VITE_CAPTCHA_KEY")}`,
              }
            : {}),
        },
        withCredentials: true,
      })
      .then((response) => {
        dispatch({
          type: LOGIN_RESET_TEMP_PSW_SUCCESS,
          payload: {
            screen: PasswordResetScreen.RESET_PASSWORD_TEMP,
            isAuthenticated: false,
            userCode: userCode,
            contractCode: contractCode,    
          },
        });
      })
      .catch((error) => {
        if (error.response?.status === 409) {
          dispatch({
            type: LOGIN_SUCCESS,
            payload: {
              ...error.response.data,
              userCode: userCode,
              contractCode: contractCode,
            },
          });
        } else if (error.response?.status === 401 && error.response?.data?.errcode === ErrorType.sessionExpired) {
          dispatch({
            type: LOGIN_SESSION_EXPIRED,
            error: error.response?.data?.errcode,
          });
        } else {
          dispatch({
            type: LOGIN_FAILURE,
            error: error.response?.data?.errcode,
          });
        }
      });
  };
};

export const performLoginForQrCode = (moreTxi: string, xOldTransaction: string) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: LOGIN_REQUEST, payload: { isAuthenticated: true } });

    const moreTxiBase64Encoded = await encryptMoreTxi(moreTxi);

    await axios
      .post(
        getApiEndpoints().LOGIN,
        {},
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            "x-client-source": "WEB",
            "x-client-risk": moreTxiBase64Encoded,
            //x-type only for app flow when the QR code is requested
            "x-type": QR_X_TYPE,
            "x-old-transaction": xOldTransaction,
          },
          withCredentials: true,
        },
      )
      .then((response) => {
        dispatch({
          type: LOGIN_RESET_TEMP_PSW_SUCCESS,
          payload: {
            screen: PasswordResetScreen.RESET_PASSWORD_TEMP,
            isAuthenticated: false,
          },
        });
      })
      .catch((error) => {
        if (error.response?.status === 409) {
          dispatch({
            type: LOGIN_SUCCESS_QR_CODE,
            payload: error.response.data,
          });
        } else if (error.response?.status === 401 && error.response?.data?.errcode === ErrorType.sessionExpired) {
            dispatch({
            type: LOGIN_SESSION_EXPIRED,
            error: error.response?.data?.errcode,
          });}
          else {
          dispatch({
            type: LOGIN_FAILURE,
            error: error.response?.data?.errcode,
          });
        }
      });
  };
};

export const performLoginForResendNotify = (
  xOldTransaction: string,
  moreTxi: string,
) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: LOGIN_REQUEST });
    const moreTxiBase64Encoded = await encryptMoreTxi(moreTxi);
    await axios
      .post(
        getApiEndpoints().LOGIN,
        {},
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            "x-client-source": "WEB",
            "x-client-risk": moreTxiBase64Encoded,
            "x-type": RESEND_NOTIFY_X_TYPE,
            "x-old-transaction": xOldTransaction,
          },
          withCredentials: true,
        },
      )
      .then((response) => {
        dispatch({
          type: LOGIN_RESET_TEMP_PSW_SUCCESS,
          payload: {
            screen: PasswordResetScreen.RESET_PASSWORD_TEMP,
            isAuthenticated: false,
          },
        });
      })
      .catch((error) => {
        if (error.response?.status === 409) {
          dispatch({
            type: RESEND_NOTIFY,
            payload: {
              ...error.response.data,
            },
          });
        } else {
          dispatch({
            type: LOGIN_FAILURE,
            error: error.response?.data?.errcode,
          });
        }
      });
  };
};

export const setScreen = (screen: string) => ({
  type: SET_SCREEN,
  payload: { screen },
});

export const setError = (error: string) => ({
  type: SET_ERROR,
  payload: { error },
});

export const cancelLoginProcedure = () => ({
  type: LOGIN_CANCEL_PROCEDURE,
});
